import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Calendar, CheckCircle, ClipboardList, BarChart, ArrowRight, MapPin } from 'lucide-react';
import * as api from '../../../api/educentialApi';

const CareerDashboard = () => {
  const [goals, setGoals] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [goalsRes, sessionsRes, planRes] = await Promise.all([
        api.getCareerGoals(),
        api.getCareerSessions(),
        api.getCareerPlan(),
      ]);
      
      setGoals(goalsRes.data || []);
      setSessions(sessionsRes.data || []);
      setPlan(planRes.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const activeGoals = goals.filter((g) => g.status === 'active');
  const upcomingSessions = sessions.filter((s) => s.status === 'PENDING' || s.status === 'CONFIRMED');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Career Counseling</h1>
              <p className="text-gray-600 mt-1">Your path to professional success</p>
            </div>
            <Link
              to="/services"
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Back to Services
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard label="Active Goals" value={activeGoals.length} icon={Target} color="indigo" />
          <StatCard label="Upcoming Sessions" value={upcomingSessions.length} icon={Calendar} color="blue" />
          <StatCard label="Completed Sessions" value={sessions.filter((s) => s.status === 'COMPLETED').length} icon={CheckCircle} color="green" />
          <StatCard label="Career Plan" value={plan ? '1' : '0'} icon={ClipboardList} color="purple" />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <ActionCard
              title="Career Assessment"
              description="Discover your career potential"
              icon={BarChart}
              link="/services/career/assessment"
              color="indigo"
            />
            <ActionCard
              title="Book Session"
              description="Schedule career counseling"
              icon={Calendar}
              link="/services/career/sessions"
              color="blue"
            />
            <ActionCard
              title="Career Plan"
              description="Create your roadmap"
              icon={MapPin}
              link="/services/career/plan"
              color="purple"
            />
            <ActionCard
              title="Set Goals"
              description="Define career milestones"
              icon={Target}
              link="/services/career/goals"
              color="green"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Career Goals */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Active Career Goals</h2>
              <Link to="/services/career/goals" className="text-indigo-600 hover:text-indigo-700 text-sm">
                View All
              </Link>
            </div>
            {activeGoals.length > 0 ? (
              <div className="space-y-4">
                {activeGoals.slice(0, 3).map((goal) => (
                  <div key={goal.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-medium text-gray-900">{goal.title}</h3>
                      <span className="text-xs text-gray-500">
                        {new Date(goal.targetDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${goal.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-600">{goal.progress}% complete</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No active goals yet</p>
                <Link to="/services/career/goals" className="text-indigo-600 hover:text-indigo-700 mt-2 inline-block">
                  Create your first goal
                </Link>
              </div>
            )}
          </div>

          {/* Upcoming Sessions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Upcoming Sessions</h2>
              <Link to="/services/career/sessions" className="text-indigo-600 hover:text-indigo-700 text-sm">
                View All
              </Link>
            </div>
            {upcomingSessions.length > 0 ? (
              <div className="space-y-3">
                {upcomingSessions.slice(0, 3).map((session, idx) => (
                  <div key={idx} className="border-l-4 border-indigo-500 pl-4 py-2">
                    <p className="font-medium text-gray-900">{session.topic}</p>
                    <p className="text-sm text-gray-600">{session.sessionType}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(session.preferredDate).toLocaleDateString()} at {session.preferredTime}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No upcoming sessions</p>
                <Link to="/services/career/sessions" className="text-indigo-600 hover:text-indigo-700 mt-2 inline-block">
                  Book a session
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Career Plan Overview */}
        {plan && (
          <div className="mt-8 bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Your Career Plan</h2>
              <Link to="/services/career/plan" className="text-indigo-600 hover:text-indigo-700 text-sm">
                View Full Plan
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Current Position</p>
                <p className="font-medium text-gray-900">{plan.currentPosition}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Target Position</p>
                <p className="font-medium text-gray-900">{plan.targetPosition}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Timeframe</p>
                <p className="font-medium text-gray-900">{plan.timeframe}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Progress</p>
                <p className="font-medium text-gray-900">
                  {plan.plan.filter((s) => s.completed).length} / {plan.plan.length} steps
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-700',
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{label}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
        </div>
        <div className={`${colors[color]} p-3 rounded-lg`}>
          <Icon className="w-8 h-8" />
        </div>
      </div>
    </div>
  );
};

const ActionCard = ({ title, description, icon: Icon, link, color }) => {
  const colors = {
    indigo: 'bg-indigo-600 hover:bg-indigo-700',
    blue: 'bg-blue-600 hover:bg-blue-700',
    purple: 'bg-purple-600 hover:bg-purple-700',
    green: 'bg-green-600 hover:bg-green-700',
  };

  return (
    <Link to={link} className={`${colors[color]} text-white rounded-lg p-6 transition-colors`}>
      <div className="mb-4">
        <Icon className="w-10 h-10" />
      </div>
      <h3 className="font-semibold text-lg mb-1">{title}</h3>
      <p className="text-sm opacity-90">{description}</p>
    </Link>
  );
};

export default CareerDashboard;
